import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from "react-redux";
import { useParams, useNavigate } from 'react-router-dom';

import { ScrollContainer } from "react-nice-scroll";
import { Button, Card, ListGroup } from 'react-bootstrap';

import { fetchTopAlerts as fetchTopAlertsService } from 'services/modules/ecs/alert';

const SignificantThreats = () => {
  // Redux dispatch
  const dispatch = useDispatch();

  // React router
  const navigate = useNavigate();

  // useParams hook
  const { category } = useParams();

  // State variables
  const [criticalThreats, setCriticalThreats] = useState([]);
  const [highThreats, setHighThreats] = useState([]);
  const [mediumThreats, setMediumThreats] = useState([]);
  let severityName = (category === "compliance" ? true : false);
  
  // Redux selectors
  const reportDateContainer = useSelector((state) => state.report.latestReportDate);
  const selectedCompanyContainer = useSelector((state) => state.auth.companyData);
  const topAlertsContainer = useSelector((state) => state.ecsAlert.topalerts);
  const dimensionsContainer = useSelector(state => state.ecsData.dimensions);
  
  // useEffect hook fetches all the top alerts based on category
  useEffect(() => {
    const fetchSignificantThreatsData = async () => {
      setCriticalThreats([]);
      setMediumThreats([]);
      setHighThreats([]);
      let params = {
        "domain": selectedCompanyContainer.domain,
        "date": reportDateContainer
      };
      if (category === "compliance") {
        params.compliance = true;
      } else if (category !== "over-all") {
        const dimension = dimensionsContainer.find((dim) => dim.slug === category);
        params.dimension = dimension.id;
      }
      dispatch(fetchTopAlertsService({ params }));
    };
    if (reportDateContainer && (dimensionsContainer.length > 0)) {
      fetchSignificantThreatsData();
    } else {
      setCriticalThreats([]);
      setMediumThreats([]);
      setHighThreats([]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [reportDateContainer, category, dimensionsContainer]);
  
  // useEffect hook sets the alerts to state variables 
  useEffect(() => {
    if (topAlertsContainer) {
      setCriticalThreats([]);
      setHighThreats([]);
      setMediumThreats([]);

      if (topAlertsContainer.critical && topAlertsContainer.critical.length > 0) {
        let critical = topAlertsContainer.critical;
        setCriticalThreats(critical);
      }
      if (topAlertsContainer.high && topAlertsContainer.high.length > 0) {
        let high = topAlertsContainer.high;
        setHighThreats(high);
      }
      if (topAlertsContainer.medium && topAlertsContainer.medium.length > 0) {
        let medium = topAlertsContainer.medium;
        setMediumThreats(medium);
      }
    }
  }, [topAlertsContainer]);
 
  // Handles navigation to single alert page
  function navigateSingleThreat(threat) {
    navigate(`/crs/threat/${threat.id}?domain=${selectedCompanyContainer.domain}`);
  }

  // Handles navigation to alerts page
  function navigateThreats(threat) {
    const domain = selectedCompanyContainer?.domain || '';
    const url = `/crs/category-threats-dashboard/category-threats?category=${category}&alert_severity=${threat}&domain=${domain}&date=${reportDateContainer}`;
    navigate(url);
  }

  return (
    <div>
      <ScrollContainer className="topalerts">
        <Card className="text-white rounded-10 topalerts">
          <Card.Body>
            <Card.Title>
              <div className="carousel-title text-white">Top issues found</div>
            </Card.Title>
            <Card.Text>
              <div className='topalerts-div'>
                <div className={severityName ? 'overAll-none' : 'overAll-active'}>
                  <div>
                    <div className="d-flex flex-wrap  items-center">
                      <div className="severity">
                        <img src='/assets/icons/critical.svg' alt="" className="significant-threats-font" />
                        Critical
                        {(!criticalThreats || criticalThreats.length === 0) ? (
                          <span className="text-h8 text-center text-tertiary">
                            No findings found
                          </span>
                        ) : null}
                      </div>
                      <div className="d-flex flex-grow-1 justify-content-end">
                        {criticalThreats && criticalThreats.length > 0 && (
                          <Button
                            variant="outline-light"
                            rounded
                            onClick={() => navigateThreats('Critical')}
                            className="default-text rounded-pill topalerts-button"
                          >
                            View all
                          </Button>
                        )}
                      </div>
                    </div>
                    <div>
                      {(criticalThreats && criticalThreats.length > 0) ? (
                        <ListGroup className='topalerts-listgroup'>
                          {criticalThreats.map((threat, index) => (
                            <div key={index}>
                              <ListGroup.Item action onClick={() => navigateSingleThreat(threat)}>
                                <div className="d-flex justify-content-between">
                                  <span>{threat.title}</span>
                                  <span className="font-weight-bold">{Number.parseFloat(threat.score).toFixed(2)}</span>
                                </div>
                              </ListGroup.Item>
                            </div>
                          ))}
                        </ListGroup>
                      ) : (
                        <div className="mt-3">
                          <hr className="my-2" />
                        </div>
                      )}
                    </div>
                  </div>
                  <div>
                    <div className="d-flex flex-wrap  items-center">
                      <div className="severity">
                        <img src='/assets/icons/high_priority.svg' alt="" />
                        High
                        {(!highThreats || highThreats.length === 0) ? (
                          <span className="text-h8 text-center text-tertiary">
                            No Findings Found
                          </span>
                        ) : null}
                      </div>
                      <div className="d-flex flex-grow-1 justify-content-end">
                        {highThreats && highThreats.length > 0 && (
                          <Button
                            variant="outline-light"
                            rounded
                            onClick={() => navigateThreats('High')}
                            className="default-text rounded-pill topalerts-button"
                          >
                            View all
                          </Button>
                        )}
                      </div>
                    </div>
                    <div>
                      {(highThreats && highThreats.length > 0) ? (
                        <ListGroup className='topalerts-listgroup'>
                          {highThreats.map((threat, index) => (
                            <div key={index}>
                              <ListGroup.Item action onClick={() => navigateSingleThreat(threat)}>
                                <div className="d-flex justify-content-between">
                                  <span>{threat.title}</span>
                                  <span className="font-weight-bold">{Number.parseFloat(threat.score).toFixed(2)}</span>
                                </div>
                              </ListGroup.Item>
                            </div>
                          ))}
                        </ListGroup>
                      ) : (
                        <div className="mt-3">
                          <hr className="my-2" />
                        </div>
                      )}
                    </div>
                  </div>
                  <div>
                    <div className="d-flex flex-wrap  items-center">
                      <div className="severity">
                        <img src='/assets/icons/medium.svg' alt="" />
                        Medium
                        {(!mediumThreats || mediumThreats.length === 0) ? (
                          <span className="text-h8 text-center text-tertiary">
                            No Findings Found
                          </span>
                        ) : null}
                      </div>
                      <div className="d-flex flex-grow-1 justify-content-end">
                        {mediumThreats && mediumThreats.length > 0 && (
                          <Button
                            variant="outline-light"
                            rounded
                            onClick={() => navigateThreats('Medium')}
                            className="default-text rounded-pill topalerts-button"
                          >
                            View all
                          </Button>
                        )}
                      </div>
                    </div>
                    <div>
                      {(mediumThreats && mediumThreats.length > 0) ? (
                        <ListGroup className='topalerts-listgroup'>
                          {mediumThreats.map((threat, index) => (
                            <div key={index}>
                              <ListGroup.Item action onClick={() => navigateSingleThreat(threat)}>
                                <div className="d-flex justify-content-between">
                                  <span>{threat.title}</span>
                                  <span className="font-weight-bold">{Number.parseFloat(threat.score).toFixed(2)}</span>
                                </div>
                              </ListGroup.Item>
                            </div>
                          ))}
                        </ListGroup>
                      ) : (
                        <div className="mt-3">
                          <hr className="my-2" />
                        </div>
                      )}
                    </div>
                  </div>
                </div>
                <div className={severityName ? 'compliance-active' : 'compliance-none'}>
                  <div>
                    <div className="d-flex flex-wrap  items-center">
                      <div className="severity">
                        <img src='/assets/icons/critical.svg' alt="" className="significant-threats-font" />
                        Non-Compliant
                        {(!criticalThreats || criticalThreats.length === 0) && (!highThreats || highThreats.length === 0) ? (
                          <span className="text-h8 text-center text-tertiary">
                            No findings found
                          </span>
                        ) : null}
                      </div>
                      <div className="d-flex flex-grow-1 justify-content-end">
                        {((criticalThreats && criticalThreats.length > 0) || (highThreats && highThreats.length > 0)) && (
                          <Button
                            variant="outline-light"
                            rounded
                            onClick={() => navigateThreats('Non-Compliant')}
                            className="default-text rounded-pill topalerts-button"
                          >
                            View all
                          </Button>
                        )}
                      </div>
                    </div>
                    <div>
                      {(criticalThreats && criticalThreats.length > 0) || (highThreats && highThreats.length > 0) ? (
                        <ListGroup className='topalerts-listgroup'>
                          {criticalThreats.map((threat, index) => (
                            <div key={index}>
                              <ListGroup.Item action onClick={() => navigateSingleThreat(threat)}>
                                <div className="d-flex justify-content-between">
                                  <span>{threat.title}</span>
                                  <span className="font-weight-bold">{Number.parseFloat(threat.score).toFixed(2)}</span>
                                </div>
                              </ListGroup.Item>
                            </div>
                          ))}
                          {highThreats.map((threat, index) => (
                            <div key={index}>
                              <ListGroup.Item action onClick={() => navigateSingleThreat(threat)}>
                                <div className="d-flex justify-content-between">
                                  <span>{threat.title}</span>
                                  <span className="font-weight-bold">{Number.parseFloat(threat.score).toFixed(2)}</span>
                                </div>
                              </ListGroup.Item>
                            </div>
                          ))}
                        </ListGroup>
                      ) : (
                        <div className="mt-3">
                          <hr className="my-2" />
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            </Card.Text>
          </Card.Body>

        </Card>
      </ScrollContainer>
    </div>
  )
}

export default SignificantThreats;
